import { useCallback, useRef, useSyncExternalStore } from 'react';

const NOTHING = 'none';

/** A row's edges as one comparable value, so a scroll only re-renders when one of them changes. */
function readEdges(row: HTMLElement | null): string {
  if (!row || row.scrollWidth <= row.clientWidth + 1) return NOTHING;
  const atStart = row.scrollLeft <= 1;
  const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
  return `${atStart ? 'start' : ''}${atEnd ? 'end' : ''}`;
}

export interface RowOverflow<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  overflowing: boolean;
  canPrevious: boolean;
  canNext: boolean;
  /** Scrolls about one view along, leaving `covered` pixels of the row's end out of the count. */
  page: (direction: -1 | 1, covered?: number) => void;
}

/** Whether a horizontally scrolling row has more to show either way, and a way to page through. */
export function useRowOverflow<T extends HTMLElement>(): RowOverflow<T> {
  const ref = useRef<T>(null);
  const subscribe = useCallback((notify: () => void) => {
    const row = ref.current;
    if (!row) return () => undefined;
    row.addEventListener('scroll', notify, { passive: true });
    const observer = new ResizeObserver(notify);
    observer.observe(row);
    return () => {
      row.removeEventListener('scroll', notify);
      observer.disconnect();
    };
  }, []);
  const edges = useSyncExternalStore(subscribe, () => readEdges(ref.current));

  const page = useCallback((direction: -1 | 1, covered = 0) => {
    const row = ref.current;
    if (!row) return;
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row.scrollBy({
      left: direction * Math.max(row.clientWidth - covered, 1),
      behavior: calm ? 'auto' : 'smooth',
    });
  }, []);

  return {
    ref,
    overflowing: edges !== NOTHING,
    canPrevious: edges !== NOTHING && !edges.includes('start'),
    canNext: edges !== NOTHING && !edges.includes('end'),
    page,
  };
}
